import { useState } from "react";
import { motion } from "framer-motion";
import { Compass, Building2, Fingerprint, Landmark, FileCheck, Plane, Home, MapPin, TrendingUp, Globe } from "lucide-react";
import { MILESTONES } from "@/data/site";
import { useConsultation } from "@/context/ConsultationContext";

const ICONS = { Compass, Building2, Fingerprint, Landmark, FileCheck, Plane, Home, MapPin, TrendingUp, Globe };

export function Roadmap() {
  const [active, setActive] = useState(0);
  const { openConsultation } = useConsultation();
  const current = MILESTONES[active];
  const ActiveIcon = ICONS[current.icon];

  return (
    <section id="roadmap" data-testid="roadmap" className="relative bg-warm text-navy">
      <div className="max-w-7xl mx-auto px-6 md:px-8 pt-28 pb-10 text-center">
        <p className="accent-eyebrow text-terracotta">The Portugal Success Roadmap</p>
        <h2 className="font-playfair text-4xl md:text-6xl mt-5 leading-tight max-w-3xl mx-auto">Ten milestones on the road to your future.</h2>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-8 grid lg:grid-cols-[0.9fr_1.1fr] gap-6 lg:gap-16 pb-28">
        {/* Sticky visual panel */}
        <div className="hidden lg:block">
          <div className="sticky top-28 h-[70vh] rounded-3xl bg-navy text-warm overflow-hidden flex flex-col justify-between p-10">
            <div className="spotlight absolute inset-0 pointer-events-none" />
            <div className="relative flex items-center justify-between">
              <span className="font-playfair text-[7rem] leading-none text-gradient-gold">{current.n}</span>
              <span className="flex h-16 w-16 items-center justify-center rounded-2xl border border-gold/40 bg-gold/10">
                <ActiveIcon className="h-8 w-8 text-gold" strokeWidth={1.4} />
              </span>
            </div>
            <div className="relative">
              <p className="accent-eyebrow text-gold">{current.theme}</p>
              <h3 className="font-playfair text-3xl mt-4">{current.title}</h3>
              <p className="text-warm/60 mt-4 leading-relaxed">{current.body}</p>
              {/* progress dots */}
              <div className="mt-8 flex gap-1.5">
                {MILESTONES.map((_, i) => (
                  <span key={i} className={`h-1 rounded-full transition-all duration-300 ${i === active ? "w-8 bg-gold" : "w-3 bg-warm/20"}`} />
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Scrolling narrative */}
        <div>
          {MILESTONES.map((m, i) => {
            const Icon = ICONS[m.icon];
            return (
              <motion.div
                key={m.n}
                onViewportEnter={() => setActive(i)}
                viewport={{ margin: "-45% 0px -45% 0px" }}
                data-testid={`milestone-${m.n}`}
                className="min-h-[62vh] flex flex-col justify-center border-b border-navy/10 py-10"
              >
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                >
                  <div className="flex items-center gap-4">
                    <span className="lg:hidden flex h-12 w-12 items-center justify-center rounded-xl border border-terracotta/40 bg-terracotta/10">
                      <Icon className="h-6 w-6 text-terracotta" strokeWidth={1.5} />
                    </span>
                    <span className="font-playfair text-5xl md:text-6xl text-navy/15">{m.n}</span>
                  </div>
                  <p className="accent-eyebrow text-terracotta mt-6">{m.theme}</p>
                  <h3 className="font-playfair text-3xl md:text-5xl mt-3 leading-tight">{m.title}</h3>
                  <p className="text-navy/60 text-lg mt-5 max-w-lg leading-relaxed">{m.body}</p>
                </motion.div>
              </motion.div>
            );
          })}
          <div className="pt-14">
            <button data-testid="roadmap-cta" onClick={() => openConsultation()} className="rounded-full bg-navy px-8 py-4 font-600 text-warm hover:bg-navy-800 transition-all hover:scale-105 active:scale-95">
              Map My Journey
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
